/* Shared drag controller: track movement outside cards and keep taps distinct. */
(() => {
  if (window.KoraDragScroll) return;
  const controllers = new WeakMap();
  window.KoraDragScroll = (rail, options = {}) => {
    if (controllers.has(rail)) return controllers.get(rail);
    const listeners = new AbortController();
    const { signal } = listeners;
    let gesture = null;
    let momentum = 0;
    let suppressClickUntil = 0;
    let pointerListeners = null;
    const maximum = () => Math.max(0, rail.scrollWidth - rail.clientWidth);
    const clamp = (left) => Math.max(0, Math.min(maximum(), left));
    const direct = (enabled) => rail.classList.toggle('kora-pan--direct', enabled);
    const cancel = () => {
      cancelAnimationFrame(momentum);
      momentum = 0;
      if (!gesture?.active) direct(false);
    };
    const glide = (speed) => {
      if (options.momentum === false || window.matchMedia('(prefers-reduced-motion: reduce)').matches || Math.abs(speed) < 0.08) {
        direct(false);
        return;
      }
      let previous = performance.now();
      let velocity = Math.max(-2.5, Math.min(2.5, speed));
      direct(true);
      const tick = (time) => {
        if (!rail.isConnected) return cancel();
        const elapsed = Math.min(40, Math.max(1, time - previous));
        previous = time;
        const before = rail.scrollLeft;
        rail.scrollLeft = clamp(before + velocity * elapsed);
        velocity *= Math.exp(-elapsed / 230);
        if (Math.abs(velocity) < 0.035 || Math.abs(rail.scrollLeft - before) < 0.5) return cancel();
        momentum = requestAnimationFrame(tick);
      };
      momentum = requestAnimationFrame(tick);
    };
    const finish = (event, cancelled = false) => {
      if (!gesture || (event?.pointerId !== undefined && event.pointerId !== gesture.id)) return;
      const ended = gesture;
      gesture = null;
      pointerListeners?.abort();
      pointerListeners = null;
      try {
        if (rail.hasPointerCapture?.(ended.id)) rail.releasePointerCapture(ended.id);
      } catch (_) { /* Document listeners already handle capture loss. */ }
      rail.classList.remove('kora-pan--dragging');
      if (!ended.active) { direct(false); return; }
      suppressClickUntil = performance.now() + 500;
      event?.stopPropagation();
      if (ended.paged) {
        direct(false);
        if (!cancelled && Math.abs(ended.lastX - ended.x) >= 30) options.onSwipe(ended.lastX < ended.x ? 1 : -1);
      } else if (!cancelled && performance.now() - ended.time < 90) {
        glide(ended.velocity);
      } else {
        direct(false);
      }
    };
    const move = (event) => {
      if (!gesture || event.pointerId !== gesture.id) return;
      if (!(event.buttons & 1)) return finish(event, true);
      const dx = event.clientX - gesture.x;
      const dy = event.clientY - gesture.y;
      if (!gesture.active) {
        if (Math.abs(dx) < 5 || Math.abs(dx) <= Math.abs(dy)) return;
        gesture.active = true;
        direct(true);
        rail.classList.add('kora-pan--dragging');
        try { rail.setPointerCapture?.(event.pointerId); } catch (_) { /* Continue with document tracking. */ }
      }
      event.preventDefault();
      event.stopPropagation();
      const time = performance.now();
      const elapsed = Math.max(1, time - gesture.time);
      const speed = (gesture.lastX - event.clientX) / elapsed;
      gesture.velocity = gesture.velocity * 0.3 + speed * 0.7;
      gesture.lastX = event.clientX;
      gesture.time = time;
      if (!gesture.paged) rail.scrollLeft = clamp(gesture.left - dx);
    };
    const start = (event) => {
      if (!rail.isConnected || !rail.contains(event.target)) return;
      cancel();
      options.onStart?.();
      if ((event.pointerType !== 'mouse' && event.pointerType !== 'pen') || event.button !== 0 || event.isPrimary === false) return;
      if (event.target.closest?.('input[type="range"], textarea, select, [contenteditable="true"]')) return;
      const paged = maximum() <= 1;
      if (paged && !options.onSwipe) return;
      if (gesture) finish(null, true);
      suppressClickUntil = 0;
      gesture = { id: event.pointerId, x: event.clientX, y: event.clientY, lastX: event.clientX,
        left: rail.scrollLeft, active: false, velocity: 0, time: performance.now(), paged };
      pointerListeners = new AbortController();
      const pointerOptions = { capture: true, passive: false, signal: pointerListeners.signal };
      document.addEventListener('pointermove', move, pointerOptions);
      document.addEventListener('pointerup', finish, pointerOptions);
      document.addEventListener('pointercancel', (e) => finish(e, true), pointerOptions);
    };
    // Capture at document level so video overlays cannot swallow a mouse drag.
    document.addEventListener('pointerdown', start, { capture: true, signal });
    document.addEventListener('click', (event) => {
      if (!rail.contains(event.target) || event.detail === 0 || performance.now() >= suppressClickUntil) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      suppressClickUntil = 0;
    }, { capture: true, signal });
    rail.addEventListener('dragstart', (event) => event.preventDefault(), { capture: true, signal });
    rail.addEventListener('wheel', cancel, { capture: true, passive: true, signal });
    window.addEventListener('blur', () => finish(null, true), { signal });
    rail.classList.add('kora-pan');
    const controller = { cancel, get dragging() { return Boolean(gesture?.active); }, destroy() {
      finish(null, true); cancel(); listeners.abort(); controllers.delete(rail); rail.classList.remove('kora-pan');
    } };
    controllers.set(rail, controller);
    return controller;
  };
})();

(() => {
  const galleryItems = (gallery) =>
    Array.from(gallery.querySelectorAll('[id^="Slider-Gallery-"] > .product__media-item[data-media-id]'));

  const activeGalleryIndex = (gallery, items) => {
    const active = gallery.querySelector('[id^="Slider-Gallery-"] > .product__media-item.is-active');
    if (active && items.includes(active)) return items.indexOf(active);

    const list = gallery.querySelector('[id^="Slider-Gallery-"]');
    if (!list || !items.length) return 0;

    const center = list.scrollLeft + list.clientWidth / 2;
    let closestIndex = 0;
    let closestDistance = Number.POSITIVE_INFINITY;
    items.forEach((item, index) => {
      const distance = Math.abs(item.offsetLeft + item.offsetWidth / 2 - center);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });
    return closestIndex;
  };

  const updateGalleryProgress = (gallery, requestedIndex) => {
    const host = gallery.querySelector('[data-kora-gallery-dots]');
    const count = gallery.querySelector('[data-kora-gallery-count]');
    const items = galleryItems(gallery);
    if (!host || !items.length) return;

    if (host.children.length !== items.length) {
      const labelTemplate = host.dataset.labelTemplate || 'View image __index__';
      host.replaceChildren(
        ...items.map((item, index) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'kora-gallery-dot';
          dot.dataset.index = String(index);
          dot.setAttribute('aria-label', labelTemplate.replace('__index__', String(index + 1)));
          dot.addEventListener('click', () => {
            item.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
          });
          return dot;
        })
      );
    }

    const index = Math.max(0, Math.min(requestedIndex ?? activeGalleryIndex(gallery, items), items.length - 1));
    const dots = Array.from(host.children);
    const visibleCount = Math.min(5, dots.length);
    const start = Math.min(Math.max(index - 2, 0), dots.length - visibleCount);
    const end = start + visibleCount;

    dots.forEach((dot, dotIndex) => {
      dot.hidden = dotIndex < start || dotIndex >= end;
      if (dotIndex === index) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });

    if (count) count.textContent = `${index + 1} / ${items.length}`;
  };

  const initGallery = (gallery) => {
    if (gallery.dataset.koraGalleryReady === 'true') return;
    const list = gallery.querySelector('[id^="Slider-Gallery-"]');
    const viewer = gallery.querySelector('[id^="GalleryViewer-"]');
    const host = gallery.querySelector('[data-kora-gallery-dots]');
    if (!list || !host) return;

    gallery.dataset.koraGalleryReady = 'true';

    const requestedVariant = new URL(window.location.href).searchParams.has('variant');
    let preserveOpeningOrder = !requestedVariant;
    const releaseOpeningOrder = () => { preserveOpeningOrder = false; };
    const resetToFirstMedia = () => {
      if (!preserveOpeningOrder || !list.isConnected) return;
      list.scrollTo({ left: 0, behavior: 'auto' });
      updateGalleryProgress(gallery, 0);
    };

    if (preserveOpeningOrder) {
      resetToFirstMedia();
      requestAnimationFrame(() => {
        resetToFirstMedia();
        requestAnimationFrame(resetToFirstMedia);
      });
      window.setTimeout(resetToFirstMedia, 250);
      window.setTimeout(resetToFirstMedia, 700);
      window.setTimeout(releaseOpeningOrder, 1200);
      ['pointerdown', 'touchstart', 'wheel', 'keydown'].forEach((type) => {
        list.addEventListener(type, releaseOpeningOrder, { once: true, passive: true });
      });
    } else {
      updateGalleryProgress(gallery);
    }

    let frame;
    list.addEventListener(
      'scroll',
      () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => updateGalleryProgress(gallery));
      },
      { passive: true }
    );

    viewer?.addEventListener('slideChanged', (event) => {
      if (preserveOpeningOrder) {
        resetToFirstMedia();
        return;
      }
      const items = galleryItems(gallery);
      const index = items.indexOf(event.detail?.currentElement);
      updateGalleryProgress(gallery, index >= 0 ? index : undefined);
    });

    const observer = new MutationObserver(() => updateGalleryProgress(gallery));
    observer.observe(list, { childList: true, attributes: true, attributeFilter: ['class'] });
  };

  const showBuyNowError = (productForm, message) => {
    if (typeof productForm?.handleErrorMessage === 'function') {
      productForm.handleErrorMessage(message);
      return;
    }
    const wrapper = productForm?.querySelector('.product-form__error-message-wrapper');
    const text = wrapper?.querySelector('.product-form__error-message');
    if (wrapper && text) {
      text.textContent = message;
      wrapper.hidden = false;
    }
  };

  const initBuyNow = (button) => {
    if (button.dataset.koraBuyNowReady === 'true') return;
    const productForm = button.closest('product-form');
    const form = productForm?.querySelector('form[data-type="add-to-cart-form"]');
    const addButton = productForm?.querySelector('.product-form__submit');
    if (!productForm || !form || !addButton) return;

    button.dataset.koraBuyNowReady = 'true';

    const syncAvailability = () => {
      button.disabled = addButton.disabled || addButton.getAttribute('aria-disabled') === 'true';
    };
    syncAvailability();
    new MutationObserver(syncAvailability).observe(addButton, {
      attributes: true,
      attributeFilter: ['disabled', 'aria-disabled'],
    });

    button.addEventListener('click', async () => {
      if (button.disabled || button.getAttribute('aria-busy') === 'true') return;
      button.setAttribute('aria-busy', 'true');
      showBuyNowError(productForm, false);

      try {
        const response = await fetch(window.routes.cart_add_url, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: new FormData(form),
        });
        const result = await response.json();
        if (!response.ok || result.status) {
          throw new Error(result.description || result.message || window.variantStrings.unavailable);
        }
        window.location.assign(button.dataset.checkoutUrl);
      } catch (error) {
        showBuyNowError(productForm, error.message);
        button.removeAttribute('aria-busy');
      }
    });
  };

  let variantScrollControlId = 0;
  const variantScrollStates = new WeakMap();
  const variantAutoScrollStates = new WeakMap();
  const variantScrollerSelector =
    '.kora-variant-card-scroll, .globo-swatch-product-detail .gcw-variant-img-swatch-with-price';


  // Spotlight follows the visible centre without changing the chosen variant.
  const spotlightStates = new WeakMap();
  const initVariantSpotlight = (rail) => {
    if (spotlightStates.has(rail)) return;
    const selector = rail.matches('.kora-variant-card-scroll')
      ? ':scope > .kora-variant-card' : ':scope > .select-option';
    let items = [];
    let frame = 0;
    let initialized = false;
    const refreshItems = () => { items = Array.from(rail.querySelectorAll(selector)); };
    const centre = (item, behavior = 'auto') => {
      const r = rail.getBoundingClientRect();
      const c = item.getBoundingClientRect();
      rail.scrollTo({left: rail.scrollLeft + c.left + c.width / 2 - r.left - rail.clientWidth / 2, behavior});
    };
    const paint = () => {
      frame = 0;
      if (!rail.isConnected || !rail.clientWidth || !items.length) return;
      const middle = rail.getBoundingClientRect().left + rail.clientWidth / 2;
      let nearest = null;
      let distance = Infinity;
      items.forEach(item => {
        const rect = item.getBoundingClientRect();
        const delta = Math.abs(rect.left + rect.width / 2 - middle);
        if (delta < distance) { distance = delta; nearest = item; }
      });
      items.forEach(item => item.classList.toggle('kora-spotlight-current', item === nearest));
    };
    const schedule = () => { if (!frame) frame = requestAnimationFrame(paint); };
    const layout = () => {
      if (!rail.isConnected) { resize.disconnect(); observer.disconnect(); return; }
      refreshItems();
      const width = rail.clientWidth;
      const cardWidth = items[0]?.offsetWidth || 0;
      if (!width || !cardWidth) return;
      if (!initialized) {
        // Start with the first colour flush left; never reserve a blank half-row.
        rail.scrollTo({left: 0, behavior: 'instant'});
        initialized = true;
      }
      schedule();
    };
    rail.classList.add('kora-spotlight-rail');
    rail.addEventListener('scroll', schedule, {passive:true});
    rail.addEventListener('change', event => {
      if (!event.target.matches('input[type="radio"]')) return;
      refreshItems();
      const item = items.find(candidate => candidate.contains(event.target) ||
        candidate.previousElementSibling === event.target);
      if (item) {
        centre(item, window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth');
        schedule();
      }
    });
    const resize = new ResizeObserver(layout);
    const observer = new MutationObserver(layout);
    resize.observe(rail);
    observer.observe(rail, {childList:true, subtree:true});
    spotlightStates.set(rail, true);
    layout();
  };

  const initVariantAutoScroll = (scroller) => {
    const existing = variantAutoScrollStates.get(scroller);
    if (existing && scroller.isConnected) return;
    if (document.documentElement.dataset.koraVariantAutoScrollStopped === 'true') return;
    const itemSelector = scroller.matches('.kora-variant-card-scroll')
      ? ':scope > .kora-variant-card'
      : ':scope > .select-option';
    const listeners = new AbortController();
    const { signal } = listeners;
    let animationTimer = 0;
    let stopped = false;
    let started = false;
    let visible = false;
    let visibilityObserver = null;
    let resizeObserver = null;
    let contentObserver = null;
    const stop = (userInitiated = false) => {
      if (userInitiated) document.documentElement.dataset.koraVariantAutoScrollStopped = 'true';
      if (stopped) return;
      stopped = true;
      window.clearInterval(animationTimer);
      visibilityObserver?.disconnect();
      resizeObserver?.disconnect();
      contentObserver?.disconnect();
      const current = scroller.scrollLeft;
      scroller.scrollTo({ left: current, behavior: 'auto' });
      scroller.classList.remove('kora-variant-auto-reveal');
    };
    ['pointerdown', 'touchstart', 'mousedown', 'click', 'wheel', 'keydown'].forEach((type) => {
      scroller.addEventListener(type, (event) => {
        if (event.isTrusted !== false) stop(true);
      }, { capture: true, passive: type !== 'keydown', signal });
    });
    const reveal = () => {
      if (started || stopped || !visible || !scroller.isConnected || document.hidden) return;
      const items = Array.from(scroller.querySelectorAll(itemSelector));
      const maximum = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
      if (items.length < 2 || maximum <= 4 || scroller.clientWidth <= 1) return;
      started = true;
      visibilityObserver?.disconnect();
      let previousTime = performance.now();
      let restartAt = 0;
      const speed = 32;
      scroller.classList.add('kora-variant-auto-reveal');
      const tick = () => {
        if (stopped || !scroller.isConnected) return stop();
        if (document.hidden) {
          previousTime = performance.now();
          return;
        }
        const time = performance.now();
        const currentMaximum = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
        if (currentMaximum <= 4) {
          previousTime = time;
          return;
        }
        if (restartAt) {
          if (time < restartAt) return;
          scroller.scrollLeft = 0;
          restartAt = 0;
          previousTime = time;
        }
        const elapsed = Math.min(160, Math.max(0, time - previousTime));
        previousTime = time;
        scroller.scrollLeft = Math.min(currentMaximum, scroller.scrollLeft + Math.max(1, speed * elapsed / 1000));
        if (scroller.scrollLeft >= currentMaximum - 1) restartAt = time + 900;
      };
      animationTimer = window.setInterval(tick, 40);
    };
    const queueReveal = () => {
      if (started || stopped || !visible) return;
      reveal();
    };
    if ('IntersectionObserver' in window) {
      visibilityObserver = new IntersectionObserver((entries) => {
        visible = entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.01);
        if (visible) queueReveal();
      }, { threshold: [0.01, 0.25] });
      visibilityObserver.observe(scroller);
    } else {
      visible = true;
      queueReveal();
    }
    resizeObserver = new ResizeObserver(queueReveal);
    resizeObserver.observe(scroller);
    contentObserver = new MutationObserver(queueReveal);
    contentObserver.observe(scroller, { childList: true, subtree: true });
    document.addEventListener('visibilitychange', queueReveal, { signal });
    variantAutoScrollStates.set(scroller, {
      stop,
      destroy: () => {
        stop();
        listeners.abort();
      },
    });
  };

  const initVariantScrollGestures = (scroller) => {
    const listeners = new AbortController();
    const { signal } = listeners;
    let dragController;
    let wheelFrame = 0;
    let wheelTarget = null;
    let lastWheelFrameTime = 0;
    const maxScroll = () => Math.max(0, scroller.scrollWidth - scroller.clientWidth);
    const clampScroll = (value) => Math.max(0, Math.min(maxScroll(), value));

    const cancelWheel = () => {
      cancelAnimationFrame(wheelFrame);
      wheelFrame = 0;
      wheelTarget = null;
      scroller.classList.remove('kora-variant-direct-scroll');
    };

    const animateWheel = (time) => {
      if (!scroller.isConnected || wheelTarget === null) {
        cancelWheel();
        return;
      }
      const elapsed = Math.min(64, Math.max(1, time - lastWheelFrameTime));
      lastWheelFrameTime = time;
      wheelTarget = clampScroll(wheelTarget);
      const remaining = wheelTarget - scroller.scrollLeft;
      if (Math.abs(remaining) < 1) {
        scroller.scrollLeft = wheelTarget;
        cancelWheel();
        return;
      }
      const amount = remaining * (1 - Math.exp(-elapsed / 55));
      scroller.scrollLeft += Math.sign(amount) * Math.max(1, Math.abs(amount));
      wheelFrame = requestAnimationFrame(animateWheel);
    };

    scroller.classList.add('kora-variant-mouse-scroll');
    scroller.addEventListener('wheel', (event) => {
      if (event.ctrlKey || event.metaKey || event.deltaX !== 0) {
        cancelWheel();
        return;
      }
      if (!event.cancelable || !event.deltaY || maxScroll() <= 1 || dragController?.dragging) return;
      const unit = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? scroller.clientWidth : 1;
      const delta = event.deltaY * unit;
      const current = scroller.scrollLeft;
      const pending = wheelTarget ?? current;
      if ((delta < 0 && current <= 1 && pending <= 1) ||
          (delta > 0 && current >= maxScroll() - 1 && pending >= maxScroll() - 1)) return;
      event.preventDefault();
      scroller.classList.add('kora-variant-direct-scroll');
      wheelTarget = clampScroll(pending + delta);
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        scroller.scrollLeft = wheelTarget;
        cancelWheel();
      } else if (!wheelFrame) {
        lastWheelFrameTime = performance.now();
        wheelFrame = requestAnimationFrame(animateWheel);
      }
    }, { passive: false, signal });

    dragController = window.KoraDragScroll(scroller, { onStart: cancelWheel });

    return {
      cancelWheel,
      destroy: () => {
        dragController.destroy();
        cancelWheel();
        listeners.abort();
        scroller.classList.remove('kora-variant-mouse-scroll');
      },
    };
  };

  const updateVariantScrollControl = (scroller, updateSize = false) => {
    const controlId = scroller.dataset.koraVariantScrollControl;
    const controls = controlId ? document.getElementById(controlId) : null;
    if (!controls || !scroller.isConnected) return;

    const scrollbar = controls.querySelector('.kora-variant-scrollbar');
    if (!scrollbar) return;
    const maxScroll = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
    const currentScroll = Math.max(0, Math.min(maxScroll, scroller.scrollLeft));
    const hasOverflow = maxScroll > 4;
    controls.hidden = !hasOverflow;
    scrollbar.disabled = !hasOverflow;
    scrollbar.value = hasOverflow ? String(Math.round(currentScroll / maxScroll * 1000)) : '0';

    if (!updateSize || !hasOverflow) return;
    const trackWidth = scrollbar.clientWidth || scroller.clientWidth;
    const visibleRatio = scroller.clientWidth / scroller.scrollWidth;
    const thumbWidth = Math.min(trackWidth, Math.max(36, trackWidth * visibleRatio));
    scrollbar.style.setProperty('--kora-scroll-thumb-width', `${thumbWidth}px`);
  };

  const initVariantScrollControl = (scroller) => {
    const existingState = variantScrollStates.get(scroller);
    if (existingState?.controls.isConnected) {
      existingState.schedule(true);
      return;
    }

    existingState?.destroy();

    delete scroller.dataset.koraVariantScrollControl;

    const host = scroller.parentElement;
    const productInfo = scroller.closest('product-info');
    if (!host || !productInfo) return;

    host.querySelectorAll(':scope > .kora-variant-scroll-controls').forEach((control) => control.remove());

    variantScrollControlId += 1;
    const controls = document.createElement('div');
    controls.id = `KoraVariantScroll-${variantScrollControlId}`;
    controls.className = 'kora-variant-scroll-controls';

    const scrollbar = document.createElement('input');
    scrollbar.type = 'range';
    scrollbar.className = 'kora-variant-scrollbar';
    scrollbar.min = '0';
    scrollbar.max = '1000';
    scrollbar.step = '1';
    scrollbar.value = '0';
    scrollbar.setAttribute('aria-label', productInfo.dataset.koraScrollLabel);

    const hint = document.createElement('p');
    hint.id = `${controls.id}-hint`;
    hint.className = 'kora-variant-scroll-hint';
    const dragHint = document.createElement('span');
    dragHint.className = 'kora-variant-scroll-hint--drag';
    dragHint.textContent = productInfo.dataset.koraScrollDragHint;
    const swipeHint = document.createElement('span');
    swipeHint.className = 'kora-variant-scroll-hint--swipe';
    swipeHint.textContent = productInfo.dataset.koraScrollSwipeHint;
    hint.append(dragHint, swipeHint);
    scrollbar.setAttribute('aria-describedby', hint.id);

    controls.append(scrollbar, hint);
    host.classList.add('kora-variant-scroll-host');
    host.append(controls);
    scroller.dataset.koraVariantScrollControl = controls.id;
    if (!scroller.id) scroller.id = `${controls.id}-list`;
    scrollbar.setAttribute('aria-controls', scroller.id);
    const gestures = initVariantScrollGestures(scroller);
    const listeners = new AbortController();
    const { signal } = listeners;
    const beginScrollbar = (event) => {
      event.stopPropagation();
      variantAutoScrollStates.get(scroller)?.stop(true);
      gestures.cancelWheel();
      scroller.classList.add('kora-variant-direct-scroll');
    };
    const endScrollbar = (event) => {
      event.stopPropagation();
      scroller.classList.remove('kora-variant-direct-scroll');
    };
    scrollbar.addEventListener('pointerdown', beginScrollbar, { signal });
    scrollbar.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') event.preventDefault();
      if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End', 'PageUp', 'PageDown'].includes(event.key)) {
        beginScrollbar(event);
      }
    }, { signal });
    scrollbar.addEventListener('input', (event) => {
      beginScrollbar(event);
      const maxScroll = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
      scroller.scrollLeft = Number(scrollbar.value) / 1000 * maxScroll;
      schedule();
    }, { signal });
    ['change', 'pointerup', 'pointercancel', 'lostpointercapture', 'blur', 'keyup'].forEach((type) => {
      scrollbar.addEventListener(type, endScrollbar, { signal });
    });
    controls.addEventListener('click', (event) => event.stopPropagation(), { signal });
    let frame = 0;
    let positionPending = false;
    const schedule = (position = false) => {
      positionPending ||= position;
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        updateVariantScrollControl(scroller, positionPending);
        positionPending = false;
      });
    };
    const onScroll = () => schedule();
    scroller.addEventListener('scroll', onScroll, { passive: true });

    const resizeObserver = new ResizeObserver(() => schedule(true));
    resizeObserver.observe(scroller);
    resizeObserver.observe(scrollbar);
    Array.from(scroller.children).forEach((item) => resizeObserver.observe(item));
    variantScrollStates.set(scroller, {
      controls,
      schedule,
      destroy: () => {
        gestures.destroy();
        listeners.abort();
        cancelAnimationFrame(frame);
        resizeObserver.disconnect();
        scroller.removeEventListener('scroll', onScroll);
        controls.remove();
      },
    });
    updateVariantScrollControl(scroller, true);
  };

  const initVariantScrollControls = (scope = document) => {
    if (scope instanceof Element && scope.matches(variantScrollerSelector)) {
      initVariantSpotlight(scope);
      initVariantScrollControl(scope);
      // Variant autoplay disabled; keep spotlight and manual gestures.
    }
    scope.querySelectorAll?.(variantScrollerSelector).forEach((scroller) => {
      initVariantSpotlight(scroller);
      initVariantScrollControl(scroller);
      // Variant autoplay disabled; keep spotlight and manual gestures.
    });
  };

  const initVariantScrollObservers = (scope = document) => {
    const productInfos = [];
    if (scope instanceof Element && scope.matches('product-info')) productInfos.push(scope);
    scope.querySelectorAll?.('product-info').forEach((productInfo) => productInfos.push(productInfo));

    productInfos.forEach((productInfo) => {
      if (productInfo.dataset.koraVariantScrollObserver === 'true') return;
      productInfo.dataset.koraVariantScrollObserver = 'true';
      let pending = false;
      new MutationObserver(() => {
        if (pending) return;
        pending = true;
        requestAnimationFrame(() => {
          pending = false;
          if (productInfo.isConnected) initVariantScrollControls(productInfo);
        });
      }).observe(productInfo, {
        childList: true,
        subtree: true,
      });
    });
  };

  const initAmazonProduct = (scope = document) => {
    scope.querySelectorAll('media-gallery').forEach(initGallery);
    scope.querySelectorAll('media-gallery').forEach((gallery) => {
      const list = gallery.querySelector('[id^="Slider-Gallery-"]');
      const thumbnails = gallery.querySelector('[id^="Slider-Thumbnails-"]');
      if (thumbnails) window.KoraDragScroll(thumbnails);
      if (list && !list.dataset.koraPhotoFocus) {
        list.dataset.koraPhotoFocus='true';
        let frame=0;
        const paint=()=>{frame=0; const r=list.getBoundingClientRect(); if(!r.width)return;
          const cards=galleryItems(gallery);
          const nearest=cards.reduce((best,c)=>{const a=c.getBoundingClientRect();const d=Math.abs((a.left+a.right-r.left-r.right)/2);return d<best.d?{c,d}:best},{c:null,d:Infinity}).c;
          cards.forEach(c=>c.classList.toggle('kora-photo-focus',c===nearest));
        };
        const queue=()=>{if(!frame)frame=requestAnimationFrame(paint)};
        list.addEventListener('scroll',queue,{passive:true}); new ResizeObserver(queue).observe(list); queue();
      }
      if (list) window.KoraDragScroll(list, { momentum: false, onSwipe: (direction) => {
        const items = galleryItems(gallery);
        const index = activeGalleryIndex(gallery, items);
        const next = items[Math.max(0, Math.min(items.length - 1, index + direction))];
        if (next && typeof gallery.setActiveMedia === 'function') gallery.setActiveMedia(next.dataset.mediaId, false);
      } });
    });
    scope.querySelectorAll('[data-kora-buy-now]').forEach(initBuyNow);
    initVariantScrollControls(scope);
    initVariantScrollObservers(scope);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAmazonProduct());
  } else {
    initAmazonProduct();
  }

  document.addEventListener('product-info:loaded', (event) => initAmazonProduct(event.target));
  document.addEventListener('shopify:section:load', (event) => initAmazonProduct(event.target));
})();

